if (!customElements.get('heimat-card-add')) {
  // Serialize card mutations: customers can move straight to the next snack,
  // while bundled cart sections are always applied in the same order as adds.
  let pendingAdds = Promise.resolve();
  let nextStatusId = 0;
  const counts = new Map();
  let fullCartKnown = false;
  subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
    const cart = event.cartData;
    if (cart?.total_price !== undefined && Array.isArray(cart.items)) {
      fullCartKnown = true;
      counts.clear();
      cart.items.forEach(item => {
        const id = String(item.variant_id);
        counts.set(id, (counts.get(id) || 0) + item.quantity);
      });
    } else if (cart?.variant_id) {
      counts.set(String(cart.variant_id), cart.quantity);
    }
    document.querySelectorAll('heimat-card-add').forEach(card => card.refreshCount());
  });

  customElements.define('heimat-card-add', class extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.button = this.querySelector('button');
        this.addLabel = this.button.getAttribute('aria-label');
        const feedback = this.querySelector('.hg-card__add-feedback');
        feedback.id = `CardAddStatus-${++nextStatusId}`;
        this.button.setAttribute('aria-describedby', feedback.id);
        this.message = this.querySelector('[data-add-message]');
        this.cartLink = this.querySelector('[data-check-cart]');
        this.counter = this.querySelector('.hg-card__add-count');
        this.initialQuantities = JSON.parse(this.dataset.cartQuantities);
        this.initialQuantities.forEach(({ id, quantity }) => {
          if (!fullCartKnown && !counts.has(id)) counts.set(id, quantity);
        });
        this.querySelector('form').addEventListener('submit', event => this.submit(event));
      }
      this.refreshCount();
    }

    disconnectedCallback() {
      clearTimeout(this.successTimer);
      const chooser = document.querySelector('heimat-pack-chooser');
      if (chooser?.owner === this) chooser.close();
    }

    refreshCount() {
      const quantity = this.initialQuantities.reduce((total, item) => total + (counts.get(item.id) || 0), 0);
      this.counter.textContent = quantity;
      this.counter.hidden = quantity < 1;
      this.button.setAttribute('aria-label', this.addLabel + (quantity ? '. ' + this.dataset.countLabel.replace('__COUNT__', quantity) : ''));
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      const template = this.querySelector('[data-pack-template]');
      const chooser = document.querySelector('heimat-pack-chooser');
      let needsChooser = this.button.hasAttribute('aria-haspopup');
      const flavors = this.closest('.hg-card').querySelector('heimat-card-flavors');
      if (!needsChooser && flavors?.dataset.sku) {
        this.busy = true;
        this.button.setAttribute('aria-busy', 'true');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        try {
          // Flavors are separate products, even when this product has one pack.
          const products = await flavors.ensureProducts();
          needsChooser = products.length !== 1;
        } catch (error) {
          // Never silently add a default when available choices are unknown.
          needsChooser = true;
        } finally {
          this.busy = false;
          this.button.removeAttribute('aria-busy');
          this.querySelector('.loading__spinner').classList.add('hidden');
        }
      }
      if (!this.isConnected) return;
      if (needsChooser) {
        if (template && chooser?.show) chooser.show(this, template);
        else window.location.assign(this.closest('.hg-card').querySelector('.hg-card__image-link').href);
      } else {
        this.queueAdd(this.dataset.variant);
      }
    }

    queueAdd(variantId, chooser, session) {
      if (this.busy) return;
      this.busy = true;
      clearTimeout(this.successTimer);
      this.dataset.state = 'loading';
      this.button.setAttribute('aria-disabled', 'true');
      this.button.setAttribute('aria-busy', 'true');
      this.querySelector('.loading__spinner').classList.remove('hidden');
      this.message.textContent = '';
      this.cartLink.hidden = true;
      pendingAdds = pendingAdds.catch(() => {}).then(() => this.add(variantId, chooser, session));
    }

    async add(variantId, chooser, session) {
      let added = false;
      try {
        const form = new FormData(this.querySelector('form'));
        form.set('id', variantId);
        form.set('sections', 'cart-drawer,cart-icon-bubble');
        form.set('sections_url', window.location.pathname);
        const response = await (window.presaleCart?.request || window.fetch)(routes.cart_add_url, {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: form,
          signal: AbortSignal.timeout(15000),
        });
        const item = await response.json();
        if (!response.ok || item.status) {
          this.showError(response.status >= 500 ? this.dataset.uncertain : (item.description || this.dataset.error), response.status >= 500, chooser, session);
          return;
        }
        added = true;
        counts.set(variantId, item.quantity);
        document.querySelectorAll('heimat-card-add').forEach(card => card.refreshCount());
        this.dataset.state = 'success';
        this.message.textContent = this.dataset.added;
        chooser?.succeed(this, session);
        this.successTimer = setTimeout(() => {
          if (this.dataset.state === 'success' && this.cartLink.hidden) this.dataset.state = 'idle';
        }, 1600);

        // A rendering/third-party listener failure must never turn a successful
        // cart mutation into a retry prompt (which could add the pack twice).
        let sectionsRendered = false;
        try {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && item.sections?.['cart-drawer'] && item.sections?.['cart-icon-bubble']) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(item, { open: false });
            sectionsRendered = true;
          } else if (item.sections?.['cart-icon-bubble']) {
            const bubble = document.getElementById('cart-icon-bubble');
            const html = new DOMParser().parseFromString(item.sections['cart-icon-bubble'], 'text/html');
            if (bubble && html.querySelector('.shopify-section')) bubble.innerHTML = html.querySelector('.shopify-section').innerHTML;
          }
          if (drawer && !sectionsRendered) this.cartLink.hidden = false;
        } catch (error) {
          console.error('Card added; cart display could not refresh', error);
          this.cartLink.hidden = false;
        }
        try {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            interaction: 'product-card',
            productVariantId: variantId,
            addedQuantity: 1,
            cartData: item,
            sectionsRendered,
          });
        } catch (error) {
          console.error('Card added; cart subscriber failed', error);
        }
      } catch (error) {
        if (!added) this.showError(this.dataset.uncertain, true, chooser, session);
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-disabled');
        this.button.removeAttribute('aria-busy');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }
    }

    showError(message, checkCart = false, chooser, session) {
      chooser?.fail(this, session, message, checkCart);
      this.dataset.state = 'error';
      this.message.textContent = message;
      this.cartLink.hidden = !checkCart;
    }
  });
}
